import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: 'easeOut'
    }
  })
};
const painPoints = [{
  number: '01',
  title: 'El cansancio que no se ve',
  text: 'No es solo el cuerpo. Es llegar a casa después de 8 horas y todavía tener que cocinar, bañar a los niños, ayudar con la tarea. Y en algún momento del día preguntarte: ¿esto es todo lo que hay para mí?'
}, {
  number: '02',
  title: 'El dinero que nunca alcanza',
  text: 'Trabajas duro. Pero el cheque desaparece antes del día 15. La renta, el carro, el daycare. Y al final del mes, nada. Ningún ahorro. Ningún colchón. Solo la esperanza de que el próximo mes sea diferente.'
}, {
  number: '03',
  title: 'Los momentos que no regresan',
  text: 'Tu hijo dijo su primera palabra. Dio sus primeros pasos. Tuvo su primera obra de teatro en la escuela. Y tú no estabas. No porque no quisieras. Sino porque no podías darte el lujo de estar.'
}];
const headlines = ['"Trabajo para pagar el daycare. Es como si trabajara para que alguien más críe a mis hijos."', '"Llevo 5 años en el mismo trabajo. El mismo sueldo. Las mismas excusas de que pronto me suben."', '"Me da vergüenza decirle a mis hijos que no hay dinero para eso. Otra vez."', '"Sé que hay algo más para mí. Pero no sé por dónde empezar."', '"Tengo miedo de intentar algo nuevo y que no funcione. Ya no puedo darme ese lujo."'];
export function PainSection() {
  return <>
      {/* Pain Mirror */}
      <section style={{
      background: 'linear-gradient(135deg, #1C1C1E 0%, #2C2C2C 100%)',
      padding: '80px 24px'
    }} data-id="element-88">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-89">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          textAlign: 'center',
          marginBottom: '56px'
        }} data-id="element-90">
            <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(28px, 3.5vw, 44px)',
            fontWeight: 700,
            color: '#C9973A',
            marginBottom: '12px',
            lineHeight: 1.3
          }} data-id="element-91">
              Sé exactamente cómo te sientes.
            </h2>
            <p style={{
            color: '#B0B0B0',
            fontFamily: "'Lato', sans-serif",
            fontSize: '17px',
            maxWidth: '520px',
            margin: '0 auto',
            lineHeight: 1.7
          }} data-id="element-92">
              Porque miles de mujeres como tú han estado exactamente en ese
              mismo lugar. Cansadas. Atrapadas. Convencidas de que no hay
              salida.
            </p>
          </motion.div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px'
        }} data-id="element-93">
            {painPoints.map((point, i) => <motion.div key={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(201,151,58,0.2)',
            borderRadius: '16px',
            padding: '32px 28px',
            position: 'relative'
          }} data-id="element-94">
                <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '56px',
              fontWeight: 900,
              color: 'rgba(201,151,58,0.1)',
              lineHeight: 1,
              marginBottom: '16px'
            }} data-id="element-95">
                  {point.number}
                </div>
                <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '20px',
              fontWeight: 700,
              color: '#FAFAF6',
              marginBottom: '12px'
            }} data-id="element-96">
                  {point.title}
                </h3>
                <p style={{
              fontFamily: "'Lato', sans-serif",
              fontSize: '15px',
              color: '#B0B0B0',
              lineHeight: 1.8,
              margin: 0
            }} data-id="element-97">
                  {point.text}
                </p>
              </motion.div>)}
          </div>

          {/* The uncomfortable truth */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} style={{
          marginTop: '48px',
          background: 'rgba(201,151,58,0.08)',
          borderLeft: '4px solid #C9973A',
          borderRadius: '0 16px 16px 0',
          padding: '28px 32px'
        }} data-id="element-98">
            <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px'
          }} data-id="element-99">
              <AlertCircle size={24} style={{
              color: '#C9973A',
              flexShrink: 0,
              marginTop: '3px'
            }} data-id="element-100" />
              <div data-id="element-101">
                <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '20px',
                fontWeight: 700,
                color: '#C9973A',
                marginBottom: '10px'
              }} data-id="element-102">
                  La verdad que nadie te dice
                </h3>
                <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: '16px',
                color: '#FAFAF6',
                lineHeight: 1.85,
                margin: 0
              }} data-id="element-103">
                  No es que te falte esfuerzo. Llevas años esforzándote.{' '}
                  <strong style={{
                  color: '#C9973A'
                }} data-id="element-104">
                    El problema es que estás dentro de un sistema diseñado para
                    que siempre necesites más horas, no más inteligencia.
                  </strong>{' '}
                  Los trabajos físicos tienen un techo. Tu tiempo no se puede
                  duplicar. Pero un producto digital sí puede venderse mil veces
                  mientras tú duermes.{' '}
                  <em style={{
                  color: '#E8DDD0'
                }} data-id="element-105">
                    La pregunta es: ¿cuándo vas a entrar a ese sistema?
                  </em>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Voices she recognizes */}
      <section style={{
      background: '#FAFAF6',
      padding: '80px 24px'
    }} data-id="element-106">
        <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }} data-id="element-107">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} style={{
          textAlign: 'center',
          marginBottom: '48px'
        }} data-id="element-108">
            <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 700,
            color: '#1B5E3B',
            marginBottom: '12px'
          }} data-id="element-109">
              ¿Has dicho alguna de estas frases?
            </h2>
            <p style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '16px',
            color: '#4A4A4A',
            maxWidth: '480px',
            margin: '0 auto'
          }} data-id="element-110">
              Si te identificas con alguna, sigue leyendo. Esto fue escrito para
              ti.
            </p>
          </motion.div>

          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }} data-id="element-111">
            {headlines.map((headline, i) => <motion.div key={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} style={{
            background: '#fff',
            borderLeft: '3px solid #1B5E3B',
            borderRadius: '0 12px 12px 0',
            padding: '20px 24px',
            boxShadow: '0 2px 12px rgba(28,28,30,0.05)'
          }} data-id="element-112">
                <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(15px, 2vw, 17px)',
              fontStyle: 'italic',
              color: '#1C1C1E',
              lineHeight: 1.6,
              margin: 0
            }} data-id="element-113">
                  {headline}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} style={{
          textAlign: 'center',
          marginTop: '40px'
        }} data-id="element-114">
            <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(18px, 2.5vw, 24px)',
            fontStyle: 'italic',
            color: '#1B5E3B',
            fontWeight: 700
          }} data-id="element-115">
              Eso se acaba hoy.
            </p>
          </motion.div>
        </div>
      </section>
    </>;
}